import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import {
  FinanceApiService,
  FinancialBook,
  JournalControlPolicyRecord,
  JournalControlPolicyVersionRecord
} from './finance-api.service';

@Component({
  standalone: true,
  selector: 'ain-finance-control-center-journal-policy',
  imports: [AinPageComponent, FormsModule, RouterLink, TableModule, ButtonModule, SelectModule, AinDatePipe],
  template: `
    <ain-page [title]="t('finance.journalPolicy.title')" [description]="t('finance.journalPolicy.description')">
      <div class="toolbar">
        <a class="back-link" [routerLink]="['/financial/control-center']">{{ t('finance.journalPolicy.backToControlCenter') }}</a>
        <button pButton type="button" [label]="t('catalog.action.refresh')" severity="secondary" (click)="loadPolicyAndVersions()"></button>
      </div>

      <section class="card filters">
        <label>
          <span>{{ t('finance.journalPolicy.fields.book') }}</span>
          <p-select
            class="field-select"
            [options]="books()"
            optionValue="id"
            [filter]="true"
            filterBy="code,name"
            [(ngModel)]="selectedBookId"
            (ngModelChange)="onBookChanged()"
            appendTo="body">
            <ng-template pTemplate="selectedItem" let-book>
              {{ book ? (book.code + ' - ' + book.name) : '-' }}
            </ng-template>
            <ng-template pTemplate="item" let-book>
              {{ book.code }} - {{ book.name }}
            </ng-template>
          </p-select>
        </label>
        <label>
          <span>{{ t('finance.journalPolicy.fields.legalEntityId') }}</span>
          <p-select
            class="field-select"
            [options]="legalEntityOptions()"
            optionLabel="label"
            optionValue="id"
            [(ngModel)]="legalEntityIdInput"
            appendTo="body">
          </p-select>
        </label>
        <label>
          <span>{{ t('finance.journalPolicy.fields.version') }}</span>
          <p-select
            class="field-select"
            [options]="versionOptions()"
            optionLabel="label"
            optionValue="value"
            [(ngModel)]="selectedVersion"
            (ngModelChange)="loadPolicy()"
            appendTo="body">
          </p-select>
        </label>
        <div class="actions">
          <button pButton type="button" [label]="t('platform.search.apply')" (click)="applyFilters()"></button>
        </div>
      </section>

      <section class="card">
        <h3>{{ t('finance.journalPolicy.current.title') }}</h3>
        @if (loadingPolicy()) {
          <p class="muted">{{ t('common.loading') }}</p>
        } @else if (!policy()) {
          <p class="muted">{{ t('finance.journalPolicy.current.empty') }}</p>
        } @else {
          <div class="summary">
            <div><strong>{{ t('finance.journalPolicy.fields.version') }}:</strong> {{ policy()?.version }}</div>
            <div><strong>{{ t('finance.journalPolicy.fields.isPersisted') }}:</strong> {{ policy()?.isPersisted ? t('common.yes') : t('common.no') }}</div>
            <div><strong>{{ t('finance.journalPolicy.fields.isActive') }}:</strong> {{ policy()?.isActive ? t('common.yes') : t('common.no') }}</div>
          </div>
          <div class="form-grid">
            <label class="check">
              <input type="checkbox" [(ngModel)]="form.enforceCreatorCannotApprove" />
              <span>{{ t('finance.journalPolicy.fields.enforceCreatorCannotApprove') }}</span>
            </label>
            <label class="check">
              <input type="checkbox" [(ngModel)]="form.enforceCreatorCannotPost" />
              <span>{{ t('finance.journalPolicy.fields.enforceCreatorCannotPost') }}</span>
            </label>
            <label class="check">
              <input type="checkbox" [(ngModel)]="form.allowPostingInSoftClosedPeriod" />
              <span>{{ t('finance.journalPolicy.fields.allowPostingInSoftClosedPeriod') }}</span>
            </label>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('common.save')" (click)="savePolicy()" [disabled]="saving() || !selectedBookId"></button>
          </div>
        }
      </section>

      <section class="card">
        <h3>{{ t('finance.journalPolicy.versions.title') }}</h3>
        @if (loadingVersions()) {
          <p class="muted">{{ t('common.loading') }}</p>
        } @else if (!versions().length) {
          <p class="muted">{{ t('finance.journalPolicy.versions.empty') }}</p>
        } @else {
          <p-table [value]="versions()">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('finance.journalPolicy.fields.version') }}</th>
                <th>{{ t('finance.journalPolicy.fields.enforceCreatorCannotApprove') }}</th>
                <th>{{ t('finance.journalPolicy.fields.enforceCreatorCannotPost') }}</th>
                <th>{{ t('finance.journalPolicy.fields.allowPostingInSoftClosedPeriod') }}</th>
                <th>{{ t('finance.journalPolicy.fields.isActive') }}</th>
                <th>{{ t('finance.journalPolicy.fields.createdAt') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ row.version }}</td>
                <td>{{ row.enforceCreatorCannotApprove ? t('common.yes') : t('common.no') }}</td>
                <td>{{ row.enforceCreatorCannotPost ? t('common.yes') : t('common.no') }}</td>
                <td>{{ row.allowPostingInSoftClosedPeriod ? t('common.yes') : t('common.no') }}</td>
                <td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td>
                <td>{{ row.createdAtUtc | ainDate }}</td>
              </tr>
            </ng-template>
          </p-table>
        }
      </section>

      @if (errorMessage()) {
        <p class="message error">{{ errorMessage() }}</p>
      }
      @if (successMessage()) {
        <p class="message success">{{ successMessage() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; gap: 10px; justify-content: space-between; align-items: center; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; display: grid; gap: 10px; margin-bottom: 10px; }
    .filters { grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); align-items: end; }
    .field-select { width: 100%; }
    label { display: grid; gap: 6px; font-size: 13px; color: #334155; }
    input[type='text'] { height: 38px; border: 1px solid #d1d5db; border-radius: 8px; padding: 0 10px; }
    .summary { display: flex; flex-wrap: wrap; gap: 12px; color: #334155; }
    .form-grid { display: grid; gap: 10px; }
    .check { display: flex; align-items: center; gap: 8px; }
    .actions { display: flex; justify-content: flex-end; }
    .muted { margin: 0; color: #64748b; }
    .message { margin: 0; font-size: 13px; }
    .error { color: #b91c1c; }
    .success { color: #166534; }
  `]
})
export class FinanceControlCenterJournalPolicyComponent implements OnInit {
  private readonly api = inject(FinanceApiService);
  private readonly localization = inject(LocalizationService);

  readonly books = signal<FinancialBook[]>([]);
  readonly policy = signal<JournalControlPolicyRecord | null>(null);
  readonly versions = signal<JournalControlPolicyVersionRecord[]>([]);
  readonly loadingPolicy = signal(false);
  readonly loadingVersions = signal(false);
  readonly saving = signal(false);
  readonly errorMessage = signal('');
  readonly successMessage = signal('');

  selectedBookId = '';
  legalEntityIdInput = '';
  selectedVersion?: number;
  form = {
    enforceCreatorCannotApprove: true,
    enforceCreatorCannotPost: true,
    allowPostingInSoftClosedPeriod: false
  };

  ngOnInit(): void {
    this.api.listBooks().subscribe({
      next: books => {
        this.books.set(books);
        this.selectedBookId = books[0]?.id ?? '';
        this.legalEntityIdInput = books[0]?.legalEntityId ?? '';
        this.loadPolicyAndVersions();
      },
      error: () => this.errorMessage.set(this.t('finance.journalPolicy.messages.loadFailed'))
    });
  }

  onBookChanged(): void {
    this.selectedVersion = undefined;
    const selectedBook = this.books().find(item => item.id === this.selectedBookId);
    this.legalEntityIdInput = selectedBook?.legalEntityId ?? '';
    this.loadPolicyAndVersions();
  }

  applyFilters(): void {
    this.loadPolicyAndVersions();
  }

  loadPolicyAndVersions(): void {
    this.loadPolicy();
    this.loadVersions();
  }

  loadPolicy(): void {
    if (!this.selectedBookId) {
      this.policy.set(null);
      return;
    }

    this.loadingPolicy.set(true);
    this.errorMessage.set('');
    this.api.getJournalControlPolicy(this.selectedBookId, this.normalizedLegalEntityId(), this.selectedVersion).subscribe({
      next: policy => {
        this.policy.set(policy);
        this.form = {
          enforceCreatorCannotApprove: policy.enforceCreatorCannotApprove,
          enforceCreatorCannotPost: policy.enforceCreatorCannotPost,
          allowPostingInSoftClosedPeriod: policy.allowPostingInSoftClosedPeriod
        };
        this.loadingPolicy.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.journalPolicy.messages.loadFailed'));
        this.loadingPolicy.set(false);
      }
    });
  }

  loadVersions(): void {
    if (!this.selectedBookId) {
      this.versions.set([]);
      return;
    }

    this.loadingVersions.set(true);
    this.api.listJournalControlPolicyVersions(this.selectedBookId, this.normalizedLegalEntityId()).subscribe({
      next: rows => {
        this.versions.set(rows);
        this.loadingVersions.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('finance.journalPolicy.messages.loadVersionsFailed'));
        this.loadingVersions.set(false);
      }
    });
  }

  savePolicy(): void {
    if (!this.selectedBookId) {
      return;
    }

    this.saving.set(true);
    this.errorMessage.set('');
    this.successMessage.set('');
    this.api.upsertJournalControlPolicy(this.selectedBookId, {
      legalEntityId: this.normalizedLegalEntityId(),
      enforceCreatorCannotApprove: this.form.enforceCreatorCannotApprove,
      enforceCreatorCannotPost: this.form.enforceCreatorCannotPost,
      allowPostingInSoftClosedPeriod: this.form.allowPostingInSoftClosedPeriod
    }).subscribe({
      next: policy => {
        this.policy.set(policy);
        this.selectedVersion = undefined;
        this.successMessage.set(this.t('finance.journalPolicy.messages.saved'));
        this.saving.set(false);
        this.loadVersions();
        this.loadPolicy();
      },
      error: () => {
        this.errorMessage.set(this.t('finance.journalPolicy.messages.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  versionOptions(): { label: string; value: number | undefined }[] {
    return [
      { label: this.t('finance.journalPolicy.version.latest'), value: undefined },
      ...this.versions().map(item => ({
        label: `${this.t('finance.journalPolicy.fields.version')} ${item.version}`,
        value: item.version
      }))
    ];
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  legalEntityOptions(): { id: string; label: string }[] {
    const ids = new Set<string>();
    for (const book of this.books()) {
      const legalEntityId = (book.legalEntityId ?? '').trim();
      if (legalEntityId) {
        ids.add(legalEntityId);
      }
    }

    const selected = this.legalEntityIdInput.trim();
    if (selected) {
      ids.add(selected);
    }

    return [
      { id: '', label: this.t('common.none') },
      ...Array.from(ids).map(id => ({ id, label: id }))
    ];
  }

  private normalizedLegalEntityId(): string | undefined {
    const value = this.legalEntityIdInput.trim();
    return value || undefined;
  }
}
